import React, { useContext, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import HomeTwoToneIcon from "@mui/icons-material/HomeTwoTone";
import AddModeratorTwoToneIcon from "@mui/icons-material/AddModeratorTwoTone";
import KeyboardDoubleArrowRightSharpIcon from "@mui/icons-material/KeyboardDoubleArrowRightSharp";
import KeyboardDoubleArrowLeftSharpIcon from "@mui/icons-material/KeyboardDoubleArrowLeftSharp";
import PersonOutlineTwoToneIcon from "@mui/icons-material/PersonOutlineTwoTone";
import SettingsTwoToneIcon from "@mui/icons-material/SettingsTwoTone";
import SupervisorAccountTwoToneIcon from '@mui/icons-material/SupervisorAccountTwoTone';
import { Collapse, Tooltip, Stack } from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import MuiAppBar from "@mui/material/AppBar";
import { tooltipClasses } from "@mui/material/Tooltip";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import { LoginContext } from "../../../contexts/loginContext";
import { CapitalizeName } from "../../../utils/capitalizeName";
import { useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import AdminDashboard from "../AdminDashboard";
import UserSubscribers from "../UserSubscribers";
import SubscribeDetail from "../SubscribeDetail";
import SubscriptionPlan from "../SubscriptionPlan";
import { Link } from "react-router-dom";
import SectorManagement from "../SectorManagement";
import SampleDashboard from "../../../components/SampleDashboard";
import MockDash from "../AdminDashboard/MockDash";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../Redux/slices/authSlice";
import SDCManagement from "../SDCManagement";
import DepartmentManagement from "../DepartmentManagement";
import UserManagement from "../UserManagement";
import AddOrganization from "../AddOrganization";
import ListOrganization from "../ListOrganization";
import RoleManagement from "../RoleManagement";
import SkillManagement from "../SkillManagement";
import PageRightsAccess from "../PageRightAccess";
import TP_interviewermgmt from "../TP_Interviewer";

const drawerWidth = 300;

const openedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  borderTopRightRadius: 30,
  borderBottomRightRadius: 30,
  backgroundColor: "#006A71",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  borderTopRightRadius: 30,
  borderBottomRightRadius: 30,
  backgroundColor: "#006A71",
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  backgroundColor: "#1E1E1E",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  borderRadius: 10,
  backgroundColor: "#1E1E1E",
  marginTop: 15,
  marginRight: 20,
  width: `calc(100% - 7%)`,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth + 40}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));
const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const AdminLandingPage = ({ title }) => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [menudata, setMenudata] = useState("Dashboard");
  const [titleText, setTitleText] = useState("System Admin Dashboard");
  const dispatch = useDispatch();
  const userName = CapitalizeName(user?.name || "");
  const navigate = useNavigate();
  const [editOrgData, setEditOrgData] = useState(null);
  const [open, setOpen] = useState(false);
  const [showAddOrganization, setShowAddOrganization] = useState(false);
  const [showSubscriptionPlan, setShowSubscriptionPlan] = useState(false);

  const icons = [
    HomeTwoToneIcon,
    AddModeratorTwoToneIcon,
    PersonOutlineTwoToneIcon,
    SettingsTwoToneIcon,
    SupervisorAccountTwoToneIcon,
  ];

  const handleListItemClick = (item) => {
    setMenudata(item);
    setTitleText(item);
    setShowAddOrganization(false);
    setShowSubscriptionPlan(false);
  };

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleOpenAddOrganization = () => {
    setMenudata("");
    setTitleText("Add Organization");
    setShowAddOrganization(true);
    setEditOrgData(null);
  };

  const handleOpenEditOrganization = (orgData) => {
    setMenudata("");
    setTitleText("Edit Organization");
    setShowAddOrganization(true);
    setEditOrgData(orgData);
  };

  const handleOpenSubscriptionPlan = () => {
    setMenudata("");
    setShowSubscriptionPlan(true);
  };

  const handleLogOut = () => {
    localStorage.clear();
    sessionStorage.clear();
    dispatch(logout());
    setTimeout(() => navigate("/"), 0);
  };

  if (!isAuthenticated || user?.role !== "Admin") {
    return null;
  }

  const adminMasterOptions = [
    "Department Management",
    "Area Management",
    "Sector Management",
    "Role Management",
    "Skill Management",
  ];

  const organizationOptions = ["Add Organization", "List Organization"];

  return (
    <Box sx={{ display: "flex", backgroundColor: "white", height: "100vh" }}>
      <AppBar position="fixed" open={open} sx={{ background: "#006A71" }}>
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              color="white"
              sx={{ fontSize: "25px", textAlign: "left" }}
            >
              {titleText}
            </Typography>
          </Box>
          <Button
            sx={{
              display: { xs: "none", sm: "flex" },
              border: "1px solid #00BFFF",
              color: "white",
              height: "45px",
              width: "148px",
              borderRadius: "10px",
              alignItems: "center",
              justifyContent: "center",
              gap: "15px",
              backgroundColor: "#273480",
            }}
          >
            <ExitToAppIcon />
            Export
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader sx={{ position: "relative" }}>
          <Box
            sx={{
              backgroundColor: "white",
              width: "100%",
              height: "100%",
              marginTop: "5%",
              marginRight: "30px",
              borderRadius: "10px 30px 0px 20px",
            }}
          >
            {isAuthenticated && open && (
              <LightTooltip title={`${user?.name || ""}`} placement="right">
                <Typography
                  sx={{
                 position: "absolute",
      top: "20px",
      left: "45%",
      transform: "translateX(-50%)", // ✅ perfect centering
      fontSize: "15px",
      fontWeight: "bold",
      color: "#1E1E1E",
      maxWidth: "250px",
      textAlign: "center",
      wordBreak: "break-word",
      lineHeight: "1.2",
    }}
  >
    Welcome 
    {userName?.length > 12 ? <br /> :" "}
    {userName}
                </Typography>
              </LightTooltip>
            )}
          </Box>
        </DrawerHeader>
        <DrawerHeader>
          <IconButton
            color="white"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ color: "white", marginRight: 1, marginLeft: 1 }}
          >
            {open ? (
              <KeyboardDoubleArrowLeftSharpIcon />
            ) : (
              <KeyboardDoubleArrowRightSharpIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <List sx={{ color: "white" }}>
          {[
            "Dashboard",
            "Admin Master",
            "User Management",
            "Organization Management",
            "TP_Interviewer Management"
          ].map((text, index) => (
            <ListItem
              key={text}
              disablePadding
              onClick={() => handleListItemClick(text)}
            >
              <ListItemButton>
                <ListItemIcon sx={{ color: "white" }}>
                  <LightTooltip title={text}>
                    {React.createElement(icons[index % icons.length])}
                  </LightTooltip>
                </ListItemIcon>
                <ListItemText primary={text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <DrawerHeader>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ color: "white", marginRight: 1 }}
          >
            <Stack direction="column" spacing={1}>
              {open ? (
                <KeyboardDoubleArrowLeftSharpIcon />
              ) : (
                <KeyboardDoubleArrowRightSharpIcon />
              )}
              {isAuthenticated && (
                <div onClick={handleLogOut} title="Logout">
                  <LogoutIcon />
                </div>
              )}
            </Stack>
          </IconButton>
        </DrawerHeader>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          minHeight: "100vh",
          backgroundColor: "white",
          overflow: "auto",
        }}
      >
        <DrawerHeader />
        {menudata === "Dashboard" && <MockDash />}
        {menudata === "Admin Master" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
              gap: 4,
              mt: 8,
              mx: 7,
            }}
          >
            {adminMasterOptions.map((option) => (
              <Card
                key={option}
                sx={{ maxWidth: 250, bgcolor: "#006A71", minHeight: 100 }}
              >
                <CardActionArea onClick={() => handleListItemClick(option)}>
                  <CardContent>
                    <Typography variant="h6" component="div">
                      {option}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
        {menudata === "Organization Management" && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
              gap: 4,
              mt: 8,
              mx: 7,
            }}
          >
            {organizationOptions.map((option) => (
              <Card
                key={option}
                sx={{ maxWidth: 250, bgcolor: "#006A71", minHeight: 100 }}
              >
                <CardActionArea onClick={() => handleListItemClick(option)}>
                  <CardContent>
                    <Typography variant="h6" component="div">
                      {option}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
        {menudata === "TP_Interviewer Management" && <TP_interviewermgmt/>}
        {menudata === "Area Management" && <SDCManagement />}
        {menudata === "Department Management" && <DepartmentManagement />}
        {menudata === "Sector Management" && <SectorManagement />}
        {menudata === "Role Management" && <RoleManagement />}
        {menudata === "Skill Management" && <SkillManagement />}
        {menudata === "User Management" && <UserManagement />}
        {menudata === "Page access rights" && <PageRightsAccess />}
        {menudata === "UserSubscribers" && <UserSubscribers />}
        {menudata === "Add Organization" && <AddOrganization />}
        {menudata === "Subscribe Detail" && (
          <SubscribeDetail
            onOpenSubscriptionPlan={handleOpenSubscriptionPlan}
          />
        )}
        {showSubscriptionPlan && <SubscriptionPlan />}
        {menudata === "List Organization" && (
          <ListOrganization
            onOpenAddOrganization={handleOpenAddOrganization}
            onOpenEditOrganization={handleOpenEditOrganization}
          />
        )}
        {showAddOrganization && <AddOrganization orgData={editOrgData} />}
      </Box>
    </Box>
  );
};

export default AdminLandingPage;
